import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import {
  ApiCreateCreditAdjustmentPayload,
  ApiCreateCreditProductPayload,
  ApiCreditAccountAdmin,
  ApiCreditProductAdmin,
  ApiCreditProductAdminListItem,
  ApiCreditProductCodeBatch,
  ApiCreditProductPresetImage,
  ApiCreditRedemptionAdminFilters,
  ApiUpdateCreditProductPayload,
  ApiUpdateCreditSystemPayload,
  CreditAccountStatus,
} from "griptonite-api-client";

import api from "@/core/api";
import queryClient from "@/core/queryClient";

export const useVenueCreditSystemQuery = (venueUuid?: string) =>
  useQuery({
    queryKey: ["venues", venueUuid, "creditSystem"],
    queryFn: () => api.credits.getVenueCreditSystem(venueUuid!),
    enabled: !!venueUuid,
  });

// Every page, since the table filters and counts in the browser. An admin sees the whole system's
// rewards, so a group of gyms can run past the API's 25 a page.
export const useCreditSystemProductsQuery = (systemSlug?: string, venueUuid?: string) =>
  useQuery({
    queryKey: ["creditSystems", systemSlug, "products", venueUuid],
    queryFn: async () => {
      const products: ApiCreditProductAdminListItem[] = [];
      for (let page = 1; ; page++) {
        const response = await api.credits.getCreditSystemProducts(
          systemSlug!,
          venueUuid!,
          {},
          page,
        );
        products.push(...response.data);
        if (!response.metadata.next) return products;
      }
    },
    enabled: !!systemSlug && !!venueUuid,
  });

// Every page, since the accounts table searches and sorts in the browser.
export const useCreditSystemAccountsQuery = (systemSlug?: string) =>
  useQuery({
    queryKey: ["creditSystems", systemSlug, "accounts"],
    queryFn: () =>
      api.collectPages<ApiCreditAccountAdmin>(() =>
        api.credits.getCreditSystemAccounts(systemSlug!),
      ),
    enabled: !!systemSlug,
  });

// A page at a time, newest first, since a busy gym hands out hundreds of rewards a month. The
// filters apply on the server, and the last page stays on screen while the next one loads.
export const useCreditSystemRedemptionsQuery = (
  systemSlug?: string,
  filters: ApiCreditRedemptionAdminFilters = {},
) =>
  useInfiniteQuery({
    queryKey: ["creditSystems", systemSlug, "redemptions", filters],
    queryFn: ({ pageParam }) =>
      api.credits.getCreditSystemRedemptions(systemSlug!, filters, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.metadata.next ? lastPageParam + 1 : undefined,
    placeholderData: keepPreviousData,
    enabled: !!systemSlug,
  });

// A page at a time, newest first, since a long-standing member's log can run to hundreds of rows.
export const useCreditAccountTransactionsQuery = (systemSlug?: string, accountUuid?: string) =>
  useInfiniteQuery({
    queryKey: ["creditSystems", systemSlug, "accounts", accountUuid, "transactions"],
    queryFn: ({ pageParam }) =>
      api.credits.getCreditSystemAccountTransactions(
        systemSlug!,
        accountUuid!,
        undefined,
        pageParam,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.metadata.next ? lastPageParam + 1 : undefined,
    enabled: !!systemSlug && !!accountUuid,
  });

// Refetches the account's log, and the accounts list for its new balance and last activity.
export const useCreateCreditAdjustmentMutation = (systemSlug: string, accountUuid: string) =>
  useMutation({
    mutationFn: (payload: ApiCreateCreditAdjustmentPayload) =>
      api.credits.createCreditSystemAccountAdjustment(systemSlug, accountUuid, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["creditSystems", systemSlug, "accounts"] }),
  });

// Only fetched for a frozen or banned account, since an active one has no change to show.
export const useCreditAccountStatusQuery = (accountUuid: string, status: CreditAccountStatus) =>
  useQuery({
    queryKey: ["creditAccounts", accountUuid, "status"],
    queryFn: () => api.credits.getCreditAccountStatus(accountUuid),
    enabled: status !== "ACTIVE",
  });

export type CreditAccountStatusAction = "freeze" | "unfreeze" | "ban";

// Refetches the accounts list and the account's status detail for the new status.
export const useChangeCreditAccountStatusMutation = (systemSlug: string, accountUuid: string) =>
  useMutation({
    mutationFn: ({ action, reason }: { action: CreditAccountStatusAction; reason?: string }) => {
      const payload = reason ? { reason } : {};
      switch (action) {
        case "freeze":
          return api.credits.freezeCreditAccount(accountUuid, payload);
        case "unfreeze":
          return api.credits.unfreezeCreditAccount(accountUuid, payload);
        case "ban":
          return api.credits.banCreditAccount(accountUuid, payload);
      }
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["creditSystems", systemSlug, "accounts"] }),
        queryClient.invalidateQueries({ queryKey: ["creditAccounts", accountUuid, "status"] }),
      ]),
  });

// A reward shows up in every venue's list, so a change refetches them all.
const invalidateSystemProducts = (systemSlug: string) =>
  queryClient.invalidateQueries({ queryKey: ["creditSystems", systemSlug, "products"] });

// The list entry stands in until the reward itself arrives, so opening one from the table doesn't
// flash a spinner. Its editable fields are the same.
export const useCreditProductQuery = (productUuid?: string) =>
  useQuery<ApiCreditProductAdmin>({
    queryKey: ["creditProducts", productUuid],
    queryFn: () => api.credits.getCreditProduct(productUuid!),
    enabled: !!productUuid,
    placeholderData: () =>
      queryClient
        .getQueriesData<ApiCreditProductAdminListItem[]>({ queryKey: ["creditSystems"] })
        .flatMap(([, products]) => products ?? [])
        .find((product) => product.uuid === productUuid),
  });

export const useCreateCreditProductMutation = (systemSlug?: string) =>
  useMutation({
    mutationFn: (payload: ApiCreateCreditProductPayload) =>
      api.credits.createCreditProduct(systemSlug!, payload),
    onSuccess: (product) => {
      queryClient.setQueryData(["creditProducts", product.uuid], product);
      invalidateSystemProducts(product.system);
    },
  });

export const useUpdateCreditProductMutation = (productUuid?: string) =>
  useMutation({
    mutationFn: (payload: ApiUpdateCreditProductPayload) =>
      api.credits.updateCreditProduct(productUuid!, payload),
    onSuccess: (product) => {
      queryClient.setQueryData(["creditProducts", product.uuid], product);
      invalidateSystemProducts(product.system);
    },
  });

export const useDeleteCreditProductMutation = (product?: ApiCreditProductAdmin) =>
  useMutation({
    mutationFn: () => api.credits.deleteCreditProduct(product!.uuid),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ["creditProducts", product!.uuid] });
      invalidateSystemProducts(product!.system);
    },
  });

// Every page at once; there are only a handful per category, and they rarely change.
export const useCreditProductPresetImagesQuery = () =>
  useQuery({
    queryKey: ["credits", "presetImages"],
    queryFn: async () => {
      const presets: ApiCreditProductPresetImage[] = [];
      for (let page = 1; ; page++) {
        const response = await api.credits.getCreditProductPresetImages(undefined, page);
        presets.push(...response.data);
        if (!response.metadata.next) return presets;
      }
    },
    staleTime: Infinity,
  });

export const useUpdateCreditSystemMutation = (venueUuid?: string, systemSlug?: string) =>
  useMutation({
    mutationFn: (payload: ApiUpdateCreditSystemPayload) =>
      api.credits.updateCreditSystem(systemSlug!, payload),
    onSuccess: (system) => {
      queryClient.setQueryData(["venues", venueUuid, "creditSystem"], system);
      // Other venues in the system cache their own copy.
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "venues" &&
          query.queryKey[1] !== venueUuid &&
          query.queryKey[2] === "creditSystem",
      });
    },
  });

// A `STICKER` reward's count at each of the given venues. The API has no endpoint for every venue
// at once, so it's one request per venue.
export const useCreditProductStockQueries = (productUuid: string, venueUuids: string[]) =>
  useQueries({
    queries: venueUuids.map((venueUuid) => ({
      queryKey: ["creditProducts", productUuid, "stock", venueUuid],
      queryFn: () => api.credits.getCreditProductVenueStock(productUuid, venueUuid),
    })),
  });

export const useSetCreditProductStockMutation = (product: ApiCreditProductAdmin) =>
  useMutation({
    mutationFn: ({ venueUuid, remaining }: { venueUuid: string; remaining: number | null }) =>
      api.credits.setCreditProductVenueStock(product.uuid, venueUuid, remaining),
    onSuccess: (stock) => {
      queryClient.setQueryData(["creditProducts", product.uuid, "stock", stock.venue], stock);
      invalidateSystemProducts(product.system);
    },
  });

// Every page, at every venue where the user has a role. Batches are uploads, so there are few.
export const useCreditProductCodeBatchesQuery = (productUuid: string) =>
  useQuery({
    queryKey: ["creditProducts", productUuid, "codeBatches"],
    queryFn: () =>
      api.collectPages<ApiCreditProductCodeBatch>(() =>
        api.credits.getCreditProductCodeBatches(productUuid),
      ),
  });

// Uploading or withdrawing codes changes the batches and the stock in the rewards table.
const invalidateCodes = (product: ApiCreditProductAdmin) => {
  queryClient.invalidateQueries({ queryKey: ["creditProducts", product.uuid, "codeBatches"] });
  invalidateSystemProducts(product.system);
};

export const useUploadCreditProductCodeBatchMutation = (product: ApiCreditProductAdmin) =>
  useMutation({
    mutationFn: ({
      venueUuid,
      file,
      filename,
      hasHeader,
    }: {
      venueUuid: string;
      file: Blob;
      filename: string;
      hasHeader: boolean;
    }) =>
      api.credits.uploadCreditProductCodeBatch(product.uuid, venueUuid, file, filename, hasHeader),
    onSuccess: () => invalidateCodes(product),
  });

export const useWithdrawCreditProductCodeBatchMutation = (product: ApiCreditProductAdmin) =>
  useMutation({
    mutationFn: (batchUuid: string) =>
      api.credits.withdrawCreditProductCodeBatch(product.uuid, batchUuid),
    onSuccess: () => invalidateCodes(product),
  });
